import type { FC } from 'react'

const cx = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ')

export type HistoryActionIconVariant = 'buy' | 'airdrop' | 'send' | 'receive'

export interface HistoryActionIconProps {
  type: HistoryActionIconVariant
  className?: string
  'aria-label'?: string
}

const ICON_SOURCES: Record<HistoryActionIconVariant, string> = {
  buy: '/icons/HistoryActionIconBuy.svg',
  airdrop: '/icons/HistoryActionIconAirdrop.svg',
  send: '/icons/HistoryActionIconSend.svg',
  receive: '/icons/HistoryActionIconReceive.svg',
}

const baseClasses = 'inline-flex size-10 items-center justify-center'

export const HistoryActionIcon: FC<HistoryActionIconProps> = ({
  type,
  className,
  'aria-label': ariaLabel,
}) => {
  const src = ICON_SOURCES[type]
  const isDecorative = !ariaLabel

  return (
    <span className={cx(baseClasses, className)}>
      <img
        src={src}
        width={14}
        height={14}
        alt={ariaLabel ?? ''}
        aria-hidden={isDecorative || undefined}
        className="block size-full"
      />
    </span>
  )
}

export const HistoryBuyIcon: FC<Omit<HistoryActionIconProps, 'type'>> = (props) => (
  <HistoryActionIcon type="buy" {...props} />
)

export const HistoryAirdropIcon: FC<Omit<HistoryActionIconProps, 'type'>> = (props) => (
  <HistoryActionIcon type="airdrop" {...props} />
)

export const HistorySendIcon: FC<Omit<HistoryActionIconProps, 'type'>> = (props) => (
  <HistoryActionIcon type="send" {...props} />
)

export const HistoryReceiveIcon: FC<Omit<HistoryActionIconProps, 'type'>> = (props) => (
  <HistoryActionIcon type="receive" {...props} />
)

export interface HistoryActionPaletteProps {
  className?: string
  order?: HistoryActionIconVariant[]
}

const DEFAULT_ORDER: HistoryActionIconVariant[] = ['buy', 'airdrop', 'send', 'receive']

export const HistoryActionPalette: FC<HistoryActionPaletteProps> = ({
  className,
  order = DEFAULT_ORDER,
}) => {
  return (
    <div
      className={cx(
        'inline-flex items-center gap-4 rounded-[32px] bg-white/80 px-5 py-3 shadow-[0_8px_16px_rgba(15,23,42,0.08)] ring-1 ring-white/80',
        className
      )}
      aria-hidden
    >
      {order.map((type) => (
        <HistoryActionIcon key={type} type={type} />
      ))}
    </div>
  )
}

export default HistoryActionIcon
